import { createContext, useContext, useState, useEffect } from 'react';
import { notifyErrorToast } from '../util/notifyActionError';

const StudioErstellenContext = createContext();

const STORAGE_KEY = 'studioErstellenData';
export const DAY_KEYS = ['Mon', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
export const DEFAULT_AVAILABILITY_TIME_SLOTS = [{ start: '07:00', end: '21:00' }];
export const FULL_DAY_TIME_SLOTS = [{ start: '00:00', end: '23:59' }];

const cloneSlots = (slots) => slots.map((slot) => ({ ...slot }));

export const createDefaultAvailability = () =>
    DAY_KEYS.reduce((availability, day) => {
        availability[day] = {
            isActive: true,
            is24Hours: false,
            timeSlots: cloneSlots(DEFAULT_AVAILABILITY_TIME_SLOTS),
        };
        return availability;
    }, {});

export const normalizeAvailabilityForWizard = (availability) => {
    const source = availability && typeof availability === 'object' ? availability : {};

    return DAY_KEYS.reduce((normalized, day) => {
        const dayData = source[day] || {};
        const is24Hours = Boolean(dayData.is24Hours);
        const validSlots = Array.isArray(dayData.timeSlots)
            ? dayData.timeSlots.filter((slot) => slot?.start && slot?.end)
            : [];
        const hasImplicitOldFullDay =
            !is24Hours &&
            validSlots.length === 1 &&
            validSlots[0].start === '00:00' &&
            validSlots[0].end === '23:59';

        normalized[day] = {
            isActive: dayData.isActive ?? true,
            is24Hours,
            timeSlots: is24Hours
                ? cloneSlots(FULL_DAY_TIME_SLOTS)
                : hasImplicitOldFullDay || validSlots.length === 0
                    ? cloneSlots(DEFAULT_AVAILABILITY_TIME_SLOTS)
                    : cloneSlots(validSlots),
        };

        return normalized;
    }, {});
};

const createInitialStudioData = () => ({
    completedSteps: [],
    standort: {
        country: '',
        street: '',
        additionalAddress: '',
        postalCode: '',
        city: ''
    },
    coordinates: null,
    validatedAddress: null,
    equipment: [],
    optionalBookableEquipment: [],
    paidEquipment: [],
    studioTypes: [],
    tagValues: [],
    selectedAmenities: [],
    // selectedTags: [],
    StudioName: '',
    description: '',
    images: [],
    cancellationPolicy: "",
    rules: [],
    studioSize: '',
    capacity: '',
    pricePerHour: '',
    minNumberOfHours: '',
    pricingType: '',
    adjustPriceByPeople: false,
    priceGroups: [],
    tieredPricing: [],
    discounts: [],
    fees: [],
    weekendSurcharge: false,
    weekendDays: [],
    weekendMode: 'percentage',
    packages: [],
    productionAreaSize: '',
    availability: createDefaultAvailability(),
});

const hasTransientOptionalEquipmentImage = (item) =>
    Boolean(item?.image && typeof item.image === 'object' && item.image.id && !item.imageKey);

/** Persisted shape for optional bookable equipment (no File / base64 — same idea as `images`). */
export const serializeOptionalBookableEquipment = (list) => {
    if (!Array.isArray(list)) return [];
    return list.map((item) => {
        const hasImageKey = typeof item.imageKey === 'string' && item.imageKey.trim().length > 0;
        const needsPhotoReupload = !hasImageKey && Boolean(
            item.needsPhotoReupload || hasTransientOptionalEquipmentImage(item)
        );
        return {
            id: item.id,
            category: item.category,
            nameModel: item.nameModel,
            quantity: item.quantity,
            pricePerPiece: item.pricePerPiece,
            description: item.description ?? '',
            /** When true, thumbnail is the category illustration (not an uploaded file). */
            defaultThumbnailFromCategory: !hasImageKey,
            imageKey: item.imageKey ?? null,
            needsPhotoReupload,
            image: null,
        };
    });
};

export const normalizeOptionalBookableFromStorage = (item) => {
    if (!item || typeof item !== 'object') return null;
    const hasImageKey = typeof item.imageKey === 'string' && item.imageKey.trim().length > 0;
    const hadLegacyLocalImage =
        item.image && typeof item.image === 'object' && item.image.id && item.image.fileName;
    const row = {
        id: item.id,
        category: item.category ?? '',
        nameModel: item.nameModel ?? '',
        quantity: typeof item.quantity === 'number' ? item.quantity : 1,
        pricePerPiece: item.pricePerPiece,
        description: item.description ?? '',
        defaultThumbnailFromCategory: !hasImageKey,
        imageKey: item.imageKey ?? null,
        needsPhotoReupload: !hasImageKey && Boolean(item.needsPhotoReupload || hadLegacyLocalImage),
        image: null,
    };
    return row;
};

export const StudioErstellenProvider = ({ children }) => {
    const [studioData, setStudioData] = useState(() => {
        const savedData = localStorage.getItem(STORAGE_KEY);
        let parsedData = null;

        try {
            parsedData = savedData ? JSON.parse(savedData) : null;
        } catch (error) {
            parsedData = null;
        }
        
        if (!parsedData) {
            return createInitialStudioData();
        }

        if (!Array.isArray(parsedData.completedSteps)) {
            parsedData.completedSteps = [];
        }
        if (!Array.isArray(parsedData.optionalBookableEquipment)) {
            parsedData.optionalBookableEquipment = [];
        }
        if (!Array.isArray(parsedData.paidEquipment)) {
            parsedData.paidEquipment = parsedData.optionalBookableEquipment || [];
        }

        return {
            ...createInitialStudioData(),
            ...parsedData,
            images: [],
            completedSteps: parsedData.completedSteps.filter((stepIndex) => stepIndex !== 8),
            coordinates: parsedData.coordinates ?? null,
            validatedAddress: parsedData.validatedAddress ?? null,
            optionalBookableEquipment: parsedData.optionalBookableEquipment
                .map(normalizeOptionalBookableFromStorage)
                .filter(Boolean),
            paidEquipment: parsedData.paidEquipment
                .map(normalizeOptionalBookableFromStorage)
                .filter(Boolean),
            availability: normalizeAvailabilityForWizard(parsedData.availability),
        };
    });

    useEffect(() => {
        try {
            const dataForStorage = {
                ...studioData,
                images: [],
                completedSteps: (studioData.completedSteps || []).filter((stepIndex) => {
                    if (stepIndex !== 8) return true;
                    return (studioData.images || []).length > 0 &&
                        studioData.images.every((img) => img?.file instanceof File);
                }),
                optionalBookableEquipment: serializeOptionalBookableEquipment(studioData.optionalBookableEquipment),
                paidEquipment: serializeOptionalBookableEquipment(studioData.paidEquipment),
                availability: normalizeAvailabilityForWizard(studioData.availability),
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(dataForStorage));
        } catch (error) {
            notifyErrorToast('Dein Studio-Entwurf konnte nicht lokal gespeichert werden.', {
                title: 'Entwurf nicht gespeichert',
            });
        }
    }, [studioData]);

    const updateStudioData = (step, data) => {
        setStudioData(prev => {
            const next = {
                ...prev,
                [step]: data
            };
            return next;
        });
    };

    const markStepCompleted = (stepIndex) => {
        setStudioData(prev => ({
            ...prev,
            completedSteps: prev.completedSteps.includes(stepIndex) 
                ? prev.completedSteps 
                : [...prev.completedSteps, stepIndex]
        }));
    };

    const clearStudioData = () => {
        setStudioData(createInitialStudioData());
        localStorage.removeItem(STORAGE_KEY);
    };

    return (
        <StudioErstellenContext.Provider value={{
            studioData,
            updateStudioData,
            markStepCompleted,
            clearStudioData
        }}>
            {children}
        </StudioErstellenContext.Provider>
    );
};

export const useStudioErstellen = () => {
    const context = useContext(StudioErstellenContext);
    if (!context) {
        throw new Error('useStudioErstellen must be used within a StudioErstellenProvider');
    }
    return context;
};
